import React from 'react';
import { t } from '@lingui/core/macro';
import { Button, Table } from 'src/components/ui';
import Icon from 'src/components/Icon';
import { SectionCard, TextField, Toggle } from 'src/components/forms';
import { ContactRole, ContactRow, MAX_OTHER_CONTACTS, OrgContactInfo, contactRows, newOtherContact } from './contacts';

type ContactField = 'name' | 'title' | 'email' | 'phone' | 'description';

const TABLE_FIELDS: ContactField[] = ['name', 'title', 'email', 'phone'];

function roleLabel(role: ContactRole): string {
  if (role === 'cfo') return t`CFO`;
  if (role === 'alternate') return t`Alternate`;
  return t`Other`;
}

function roleTitle(role: ContactRole): string | undefined {
  if (role === 'cfo') return t`Chief Financial Officer (or equivalent)`;
  if (role === 'alternate') return t`Alternate contact for inquiries about reports`;
  return undefined;
}

function fieldLabel(field: ContactField): string {
  if (field === 'name') return t`Name`;
  if (field === 'title') return t`Title`;
  if (field === 'email') return t`Email`;
  if (field === 'phone') return t`Phone`;
  return t`Description or schedule that contact manages`;
}

export type ContactsSectionProps = {
  info: OrgContactInfo;
  setField: (path: string, value: unknown) => void;
  errorFor: (path: string) => string | undefined;
  readOnly?: boolean;
  /** Called after an "Other" row is removed, so index-keyed state (touched errors) can be reset. */
  onRowsRemoved?: () => void;
};

/**
 * The report contacts as one table: the CFO and the alternate contact are pinned rows that cannot be
 * removed, followed by up to ten other contacts. Phones get one card per contact.
 */
export function ContactsSection({ info, setField, errorFor, readOnly, onRowsRemoved }: ContactsSectionProps) {
  const rows = contactRows(info);
  const others = info.otherContacts;
  const listError = errorFor('orgContactInfo.otherContacts');

  const addContact = () => setField('orgContactInfo.otherContacts', [...others, newOtherContact()]);
  const removeContact = (index: number) => {
    setField(
      'orgContactInfo.otherContacts',
      others.filter((_, i) => i !== index)
    );
    onRowsRemoved?.();
  };

  const cell = (row: ContactRow, field: ContactField, suffix: string) => {
    const id = `contact-${row.key}-${field}${suffix}`;
    const value = row.contact[field];
    if (readOnly) {
      return (
        <span id={id} className="text-sm break-words">
          {value ? String(value) : '—'}
        </span>
      );
    }
    return (
      <TextField
        id={id}
        label={<span className="sr-only">{`${roleLabel(row.role)}: ${fieldLabel(field)}`}</span>}
        width="full"
        type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
        value={value as string | null | undefined}
        error={errorFor(`${row.path}.${field}`)}
        onChange={next => setField(`${row.path}.${field}`, next)}
      />
    );
  };

  const emergency = (row: ContactRow, suffix: string) => (
    <Toggle
      id={`contact-${row.key}-emergency-toggle${suffix}`}
      data-testid={`contact-${row.key}-emergency${suffix}`}
      aria-labelledby={suffix ? `contact-${row.key}-emergency-label${suffix}` : 'contacts-emergency-header'}
      checked={!!row.contact.emergencyContact}
      readOnly={readOnly}
      onChange={readOnly ? undefined : checked => setField(`${row.path}.emergencyContact`, checked)}
    />
  );

  const remove = (row: ContactRow, suffix: string) =>
    readOnly || row.otherIndex === null ? null : (
      <Button
        color="link"
        aria-label={t`Remove contact`}
        onClick={() => removeContact(row.otherIndex as number)}
        data-testid={`contact-${row.key}-remove${suffix}`}
      >
        <Icon icon="X" weight="regular" />
      </Button>
    );

  return (
    <SectionCard
      span={12}
      title={t`Report contacts`}
      hint={t`For condition and results reports, provide contact information for (1) the Chief Financial Officer (or equivalent) of the institution responsible for such reports, and (2) the person at the institution—other than the CFO—to whom inquiries about the reports should be directed.`}
      actions={
        readOnly ? null : (
          <>
            <span className="text-xs opacity-70">{t`${others.length} / ${MAX_OTHER_CONTACTS} others`}</span>
            <Button
              color="secondary"
              outline
              className="nowrap gap-1"
              onClick={addContact}
              disabled={others.length >= MAX_OTHER_CONTACTS}
              data-testid="contacts-add"
            >
              <Icon icon="Plus" weight="regular" /> {t`Add contact`}
            </Button>
          </>
        )
      }
    >
      {listError ? (
        <div className="text-xs text-red-600 mb-2" role="alert" data-field-error="true">
          {listError}
        </div>
      ) : null}

      {/* Wrapper carries the breakpoint: the ui Table's `table` class collides with Tailwind's display utility. */}
      <div className="hidden md:block">
        <Table borderless striped className="mb-0! text-sm">
          <thead>
            <tr className="text-xs opacity-70">
              <th className="text-left">{t`Role`}</th>
              {TABLE_FIELDS.map(field => (
                <th key={field} className="text-left">
                  {fieldLabel(field)}
                </th>
              ))}
              <th className="text-left" title={fieldLabel('description')}>
                {t`Handles`}
              </th>
              <th className="text-center" id="contacts-emergency-header">
                {t`Emergency Contact`}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key}>
                <td className="text-xs font-semibold whitespace-nowrap" title={roleTitle(row.role)}>
                  {roleLabel(row.role)}
                </td>
                {TABLE_FIELDS.map(field => (
                  <td key={field} className="min-w-[9rem]">
                    {cell(row, field, '')}
                  </td>
                ))}
                <td className="min-w-[9rem]">{row.otherIndex === null ? <span className="opacity-50">—</span> : cell(row, 'description', '')}</td>
                <td className="text-center">{emergency(row, '')}</td>
                <td className="text-right">{remove(row, '')}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="md:hidden flex flex-col gap-2">
        {rows.map(row => (
          <div key={row.key} className="border rounded-md p-2 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold" title={roleTitle(row.role)}>
                {roleLabel(row.role)}
              </span>
              <span className="ml-auto">{remove(row, '-m')}</span>
            </div>
            {TABLE_FIELDS.map(field => (
              <React.Fragment key={field}>{cell(row, field, '-m')}</React.Fragment>
            ))}
            {row.otherIndex === null ? null : cell(row, 'description', '-m')}
            <span className="flex items-center gap-2 text-xs">
              {emergency(row, '-m')}
              <span id={`contact-${row.key}-emergency-label-m`}>{t`Emergency Contact`}</span>
            </span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

export default ContactsSection;
